import { useState } from "react";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import type { LucideIcon } from "lucide-react";
import { Download, Play, Share2, Trash2 } from "lucide-react";

export type DetailHeaderActionsProps = {
  isLoading: boolean;
  isPrimaryPending?: boolean;
  isExporting?: boolean;
  isDeleting?: boolean;
  onPrimary?: () => void;
  onExport?: () => void;
  onDelete?: () => void;
  showPrimary?: boolean;
  primaryLabel?: string;
  primaryIcon?: LucideIcon;
  isPrimaryDisabled?: boolean;
  showShare?: boolean;
  showExport?: boolean;
  showDelete?: boolean;
  deleteTitle?: string;
  deleteDescription?: string;
  deleteConfirmLabel?: string;
};

export function DetailHeaderActions({
  isLoading,
  isPrimaryPending = false,
  isExporting = false,
  isDeleting = false,
  onPrimary,
  onExport,
  onDelete,
  showPrimary = true,
  primaryLabel = "Run",
  primaryIcon: PrimaryIcon = Play,
  isPrimaryDisabled,
  showShare = true,
  showExport = true,
  showDelete = true,
  deleteTitle = "Delete item",
  deleteDescription = "Are you sure you want to delete this item? This action cannot be undone.",
  deleteConfirmLabel = "Delete",
}: DetailHeaderActionsProps) {
  const [isCopied, setIsCopied] = useState(false);
  const defaultPrimaryDisabled = isLoading || isPrimaryPending || !onPrimary;
  const resolvedPrimaryDisabled = isPrimaryDisabled ?? defaultPrimaryDisabled;

  const handleShare = async () => {
    try {
      const url = window.location.href;
      await navigator.clipboard.writeText(url);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
    }
  };

  return (
    <div className="flex gap-2">
      {showPrimary && (
        <Button
          variant="default"
          onClick={onPrimary}
          disabled={resolvedPrimaryDisabled}
        >
          <PrimaryIcon
            className={`h-4 w-4 ${isPrimaryPending ? "animate-pulse" : ""}`}
          />{" "}
          {primaryLabel}
        </Button>
      )}
      {showShare && (
        <Tooltip open={isCopied} onOpenChange={() => {}}>
          <TooltipTrigger asChild>
            <Button
              variant="secondary"
              onClick={handleShare}
              disabled={isLoading}
              aria-label="Copy page link"
            >
              <Share2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Copied!</p>
          </TooltipContent>
        </Tooltip>
      )}
      {showExport && onExport && (
        <Button
          variant="secondary"
          onClick={onExport}
          disabled={isLoading || isExporting}
          aria-label="Export"
        >
          <Download
            className={`h-4 w-4 ${isExporting ? "animate-pulse" : ""}`}
          />
        </Button>
      )}
      {showDelete && onDelete && (
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="secondary"
              disabled={isLoading || isDeleting}
              aria-label={deleteTitle}
            >
              <Trash2
                className={`h-4 w-4 ${isDeleting ? "animate-pulse" : ""}`}
              />
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{deleteTitle}</DialogTitle>
              <DialogDescription>{deleteDescription}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogTrigger asChild>
                <Button variant="secondary">Cancel</Button>
              </DialogTrigger>
              <Button variant="destructive" onClick={onDelete}>
                {deleteConfirmLabel}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
